import React from "react";
import { Link } from "react-router-dom";
import { Activity, Bot, Headphones, Laptop, ShieldCheck, ArrowRight, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";

const iconMap = [Activity, Bot, Headphones, Laptop, ShieldCheck];

const featureLists = {
  en: [
    ["Ticket triage and routing", "First-response customer communication", "KB-driven standard resolution", "Multichannel handling (email / chat / portal)"],
    ["Log review and root-cause investigation", "API request / response analysis", "FTP / SFTP troubleshooting", "Reproducible cases for engineering"],
    ["Priority triage for incidents", "Structured escalation to L2 / L3", "Context-rich handover to engineering", "Post-incident follow-up with the customer"],
    ["Runbooks and SOPs", "Internal knowledge base upkeep", "Ticket hygiene and tagging", "Clean handover and shift notes"],
    ["Jira and ServiceNow", "Zendesk", "Salesforce", "We work inside your stack   no new tools forced"],
  ],
  bg: [
    ["Триаж и маршрутизиране на тикети", "Първоначален отговор към клиента", "Решение по knowledge base", "Мултиканално обработване (имейл / чат / портал)"],
    ["Преглед на логове и root-cause анализ", "Анализ на API request / response", "FTP / SFTP отстраняване на проблеми", "Възпроизводими случаи за engineering"],
    ["Приоритетен триаж на инциденти", "Структурирана ескалация към L2 / L3", "Контекстно богат handover към engineering", "Follow-up с клиента след инцидент"],
    ["Runbook-ове и SOP-и", "Поддръжка на вътрешна knowledge base", "Ticket hygiene и тагване", "Чист handover и shift notes"],
    ["Jira и ServiceNow", "Zendesk", "Salesforce", "Работим вътре в стека ви   без налагане на нови инструменти"],
  ],
};

const ServicesPage = () => {
  const { t, lang } = useI18n();
  const services = t("home.pillars.items");
  const lists = featureLists[lang] || featureLists.en;

  return (
    <div data-testid="page-services">
      <section className="relative overflow-hidden pt-8 pb-10 md:pt-14 md:pb-16">
        <div className="absolute inset-0 grid-mesh opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl reveal">
            <div className="font-mono-accent text-[11px] text-muted-foreground">{t("services.overline")}</div>
            <h1 className="mt-4 font-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              {t("services.title")}
            </h1>
            <p className="mt-5 text-muted-foreground md:text-lg max-w-2xl">
              {t("services.sub")}
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              {t("services.pilot.title")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              {t("services.pilot.sub")}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative overflow-hidden rounded-sm glass-card p-7 md:p-10">
              <div className="absolute inset-0 dot-mesh opacity-50 pointer-events-none" />
              <div className="relative">
                <h3 className="font-display text-xl font-semibold text-brand-navy dark:text-brand-cyan mb-6">
                  {t("services.pilot.whatWeCanPilot")}
                </h3>
                <ul className="space-y-3">
                  {t("services.pilot.pilotItems").map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 text-brand-cyan shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-sm glass-card p-7 md:p-10">
              <div className="absolute inset-0 dot-mesh opacity-50 pointer-events-none" />
              <div className="relative">
                <h3 className="font-display text-xl font-semibold text-brand-navy dark:text-brand-cyan mb-6">
                  {t("services.pilot.pilotOutputs")}
                </h3>
                <ul className="space-y-3">
                  {t("services.pilot.outputItems").map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 text-brand-cyan shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 space-y-4 md:space-y-6">
          {services.map((s, i) => {
            const Icon = iconMap[i % iconMap.length];
            const reverse = i % 2 === 1;
            return (
              <article
                key={s.tag}
                data-testid={`service-card-${i}`}
                className="group relative overflow-hidden rounded-sm glass-card transition duration-300 hover:-translate-y-0.5"
              >
                <div className="absolute inset-0 dot-mesh opacity-50 pointer-events-none" />
                <div className={`relative grid gap-8 md:grid-cols-12 p-7 md:p-10 ${reverse ? "md:[&>div:first-child]:order-2" : ""}`}>
                  <div className="md:col-span-5 flex flex-col justify-between gap-6">
                    <div>
                      <div className="inline-flex items-center gap-2 font-mono-accent text-[11px] text-brand-cyan">
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" /> {s.tag} · TIER
                      </div>
                      <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">
                        {s.title}
                      </h2>
                      <p className="mt-3 text-muted-foreground leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-brand-navy text-brand-cyan ring-1 ring-brand-cyan/30">
                        <Icon className="h-5 w-5" />
                      </div>
                      <Link to="/contact" data-testid={`service-cta-${i}`}>
                        <Button
                          variant="outline"
                          className="rounded-sm font-mono-accent text-xs border-brand-navy/30 hover:border-brand-cyan hover:text-brand-cyan dark:border-brand-cyan/30 btn-press"
                        >
                          {t("common.getStarted")} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <ul className="grid sm:grid-cols-2 gap-3">
                      {(lists[i] || []).map((f, j) => (
                        <li
                          key={j}
                          data-testid={`service-${i}-feature-${j}`}
                          className="flex items-start gap-3 rounded-sm border border-border/70 bg-background/40 p-3.5 backdrop-blur-sm"
                        >
                          <Check className="mt-0.5 h-4 w-4 text-brand-cyan shrink-0" />
                          <span className="text-sm leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
